<template>
  <div class="app">
    <div v-if="deadlineAlert" class="system-alert no-print" :class="{ expired: deadlineAlert.expired }">
      {{ deadlineAlert.message }}
    </div>
    <div v-if="rangeAlert" class="system-alert no-print" :class="{ expired: rangeAlert.expired }">
      {{ rangeAlert.message }}
    </div>

    <div class="toolbar no-print">
      <div class="btn-group">
        <button class="btn-primary" type="button" @click="configVisible = !configVisible">
          Configuración y Productos
        </button>
        <button class="btn-success" type="button" :disabled="isIssuanceBlocked" :title="issuanceBlockReason || 'Emitir un nuevo documento'" @click="nuevaFactura">
          {{ isIssuanceBlocked ? 'Emisión bloqueada' : 'Nuevo Documento' }}
        </button>
        <button class="btn-primary" type="button" :disabled="savingFactura" @click="guardarFactura">
          {{ savingFactura ? 'Guardando...' : 'Guardar Factura' }}
        </button>
        <button class="btn-secondary" type="button" @click="toggleFacturasPanel">
          Facturas Guardadas
        </button>
      </div>
      <div class="btn-group">
        <button class="btn-warning" type="button" @click="imprimir">
          Imprimir / PDF
        </button>
      </div>
    </div>

    <div v-show="facturasPanelVisible" class="saved-invoices-panel no-print">
      <div class="panel-heading">
        <div>
          <h3>Facturas Guardadas</h3>
          <p>Selecciona una factura para volver a cargar todos sus datos en el formulario.</p>
        </div>
        <button class="btn-secondary" type="button" :disabled="facturasLoading" @click="loadFacturas">
          {{ facturasLoading ? 'Cargando...' : 'Actualizar' }}
        </button>
      </div>

      <div v-if="apiMessage" class="api-message">{{ apiMessage }}</div>

      <div class="table-responsive">
        <table class="saved-invoices-table">
          <thead>
            <tr>
              <th>No. Factura</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>RTN Cliente</th>
              <th>Total L.</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!facturas.length">
              <td colspan="6">No hay facturas guardadas.</td>
            </tr>
            <tr v-for="factura in facturas" :key="factura.id">
              <td>{{ factura.numero }}</td>
              <td>{{ formatFechaDisplay(factura.fecha) }}</td>
              <td>{{ factura.cliente || '---' }}</td>
              <td>{{ factura.rtn_cliente || '---' }}</td>
              <td>{{ toMoney(factura.total) }}</td>
              <td>
                <button class="btn-primary table-button" type="button" @click="verFactura(factura.id)">
                  Ver
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-show="configVisible" class="config-panel no-print">
      <h3>Datos del Emisor</h3>
      <div class="config-grid">
        <div class="field">
          <label for="cfgName">Nombre Negocio</label>
          <input id="cfgName" v-model="config.name" type="text">
        </div>
        <div class="field">
          <label for="cfgRtn">RTN</label>
          <input id="cfgRtn" v-model="config.rtn" type="text">
        </div>
        <div class="field">
          <label for="cfgEmail">Correo electrónico</label>
          <input id="cfgEmail" v-model="config.email" type="email">
        </div>
        <div class="field">
          <label for="cfgDireccion">Dirección</label>
          <input id="cfgDireccion" v-model="config.direccion" type="text">
        </div>
        <div class="field">
          <label for="cfgCai">CAI</label>
          <input id="cfgCai" v-model="config.cai" type="text">
        </div>
        <div class="field">
          <label for="cfgFechaLimite">Fecha límite de emisión</label>
          <input id="cfgFechaLimite" v-model="config.fechaLimite" type="date">
        </div>
        <div class="field">
          <label for="cfgEstablecimiento">Establecimiento</label>
          <input id="cfgEstablecimiento" v-model="config.establecimiento" maxlength="3" placeholder="001" type="text" @blur="config.establecimiento = padLeft(config.establecimiento, 3)">
        </div>
        <div class="field">
          <label for="cfgPuntoEmision">Punto de emisión</label>
          <input id="cfgPuntoEmision" v-model="config.puntoEmision" maxlength="3" placeholder="001" type="text" @blur="config.puntoEmision = padLeft(config.puntoEmision, 3)">
        </div>
        <div class="field">
          <label for="cfgTipoDocumento">Tipo de documento fiscal</label>
          <select id="cfgTipoDocumento" v-model="config.tipoDocumento">
            <option value="01">01 - Factura</option>
            <option value="02">02 - Factura Prevalorada</option>
            <option value="03">03 - Ticket</option>
            <option value="04">04 - Recibo por Honorarios Profesionales</option>
            <option value="05">05 - Boleta de Compra</option>
            <option value="06">06 - Nota de Crédito</option>
            <option value="07">07 - Nota de Débito</option>
            <option value="08">08 - Comprobante de Retención</option>
            <option value="09">09 - Guía de Remisión</option>
          </select>
        </div>
        <div class="field">
          <label for="cfgCorr">Correlativo Actual</label>
          <input id="cfgCorr" v-model="config.corr" inputmode="numeric" max="99999999" min="1" placeholder="1" step="1" type="number" @input="limitCorrelativo">
        </div>
        <div class="field">
          <label for="cfgRangeFrom">Rango Desde</label>
          <input id="cfgRangeFrom" v-model="config.rangeFrom" type="text" :placeholder="`${fiscalPrefix}00000001`">
        </div>
        <div class="field">
          <label for="cfgRangeTo">Rango Hasta</label>
          <input id="cfgRangeTo" v-model="config.rangeTo" type="text" :placeholder="`${fiscalPrefix}00000100`">
        </div>
      </div>

      <div class="field logo-config">
        <label for="cfgLogoFile">Logo de la empresa</label>
        <input id="cfgLogoFile" ref="logoInput" type="file" accept="image/*" @change="handleLogoUpload">
        <div class="logo-preview-wrap">
          <div class="logo-preview-box">
            <img v-if="config.logo" :src="config.logo" alt="Logo">
            <div v-else class="logo-preview-placeholder">Sin logo cargado</div>
          </div>
          <button type="button" class="btn-danger" @click="removeLogo">Quitar Logo</button>
        </div>
      </div>

      <hr>

      <h3>Base de Datos de Productos</h3>
      <div class="product-manager">
        <div class="product-form">
          <div class="field">
            <label for="pName">Nombre Producto/Servicio</label>
            <input id="pName" v-model.trim="newProduct.name" type="text" placeholder="Ej. Consultoría">
          </div>

          <div class="field">
            <label for="pPrice">Precio Base L.</label>
            <input id="pPrice" v-model="newProduct.price" type="number" placeholder="0.00">
          </div>

          <div class="field">
            <label for="pTaxType">Condición tributaria</label>
            <select id="pTaxType" v-model="newProduct.taxType">
              <option value="exento">Exento</option>
              <option value="exonerado">Exonerado</option>
              <option value="gravado15">Gravado 15%</option>
              <option value="gravado18">Gravado 18%</option>
            </select>
          </div>

          <button class="btn-success product-add-button" type="button" @click="addProduct">
            + Agregar a la Base
          </button>
        </div>

        <div class="table-responsive">
          <table class="prod-list-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Precio</th>
                <th>Condición tributaria</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!config.products.length">
                <td colspan="4">No hay productos agregados.</td>
              </tr>
              <tr v-for="(product, index) in config.products" :key="`${product.name}-${index}`">
                <td>{{ product.name }}</td>
                <td>L. {{ toMoney(product.price) }}</td>
                <td>{{ getTaxLabel(product.taxType) }}</td>
                <td>
                  <button class="btn-danger table-button" type="button" @click="removeProduct(index)">
                    Eliminar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="btn-group config-actions">
        <button class="btn-primary" type="button" @click="guardarTodo">Guardar Cambios Globales</button>
      </div>
    </div>

    <div class="invoice-container">
      <div class="invoice-border">
        <div class="header-grid">
          <div class="company-header">
            <div class="company-logo-box">
              <img v-if="config.logo" :src="config.logo" alt="Logo empresa">
              <div v-else class="company-logo-placeholder">LOGO EMPRESA</div>
            </div>

            <div class="company-details">
              <h1>{{ config.name || '---' }}</h1>
              <p><strong>RTN:</strong> <span>{{ config.rtn || '---' }}</span></p>
              <p><strong>Correo:</strong> <span>{{ config.email || '---' }}</span></p>
              <p><strong>Dirección:</strong> <span>{{ config.direccion || '---' }}</span></p>
            </div>
          </div>

          <div class="right-header">
            <div class="auth-box">
              <p><strong>CAI:</strong> <span>{{ config.cai || '---' }}</span></p>
              <p><strong>Fecha límite de emisión:</strong> <span>{{ formattedFechaLimite }}</span></p>
              <p><strong>Rango autorizado:</strong> <span>{{ rangoTexto }}</span></p>
            </div>
            <div class="factura-titulo">{{ documentTypeLabel.toUpperCase() }}</div>
            <div class="factura-numero">{{ facturaNumero }}</div>
          </div>
        </div>

        <div class="client-grid">
          <div class="info-cell">
            <label for="fFecha">FECHA:</label>
            <input id="fFecha" v-model="invoice.fecha" type="date">
          </div>
          <div class="info-cell">
            <label for="fRtn">RTN CLIENTE:</label>
            <input id="fRtn" v-model="invoice.rtn" type="text">
          </div>
          <div class="info-cell client-name">
            <label for="fCliente">CLIENTE:</label>
            <input id="fCliente" v-model="invoice.cliente" type="text">
          </div>
          <div class="info-cell">
            <label for="fDireccionCliente">DIRECCIÓN CLIENTE:</label>
            <input id="fDireccionCliente" v-model="invoice.direccionCliente" type="text">
          </div>
          <div class="info-cell">
            <label for="fFormaPago">FORMA DE PAGO:</label>
            <select id="fFormaPago" v-model="invoice.formaPago">
              <option value="efectivo">Efectivo</option>
              <option value="credito">Crédito</option>
            </select>
          </div>
          <div v-if="invoice.formaPago === 'credito'" class="info-cell payment-due">
            <label for="fFechaVencimiento">FECHA DE VENCIMIENTO:</label>
            <input id="fFechaVencimiento" v-model="invoice.fechaVencimiento" type="date">
          </div>
        </div>

        <div class="invoice-table-wrap">
          <table class="items-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Cant.</th>
                <th>Precio L.</th>
                <th>Descuentos y rebajas L.</th>
                <th class="hide-print-tax">Condición tributaria</th>
                <th>Total L.</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="index" :class="{ 'empty-row': isEmptyRow(row) }">
                <td>
                  <input
                    v-model="row.desc"
                    class="desc"
                    list="productList"
                    type="text"
                    placeholder="Escribe para buscar..."
                    @input="handleSearch(row)"
                  >
                </td>
                <td><input v-model="row.qty" class="qty text-center" min="0" step="1" type="number"></td>
                <td>
                  <input
                    v-model="row.price"
                    class="price text-right"
                    inputmode="decimal"
                    type="text"
                    @blur="formatRowMoney(row, 'price')"
                  >
                </td>
                <td>
                  <input
                    v-model="row.discount"
                    class="discount text-right"
                    inputmode="decimal"
                    type="text"
                    @blur="formatRowMoney(row, 'discount')"
                  >
                </td>
                <td class="hide-print-tax">
                  <select v-model="row.taxType" class="tax-type">
                    <option value="exento">Exento</option>
                    <option value="exonerado">Exonerado</option>
                    <option value="gravado15">Gravado 15%</option>
                    <option value="gravado18">Gravado 18%</option>
                  </select>
                </td>
                <td class="row-total">{{ isEmptyRow(row) ? '' : toMoney(rowTotal(row)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="no-print manual-row">
          <button class="btn-secondary" type="button" @click="addRow">+ Fila Manual</button>
        </div>

        <div class="totals-section">
          <div>
            <div class="letters-box">
              <strong>VALOR EN LETRAS:</strong>
              <div class="letters-value">
                {{ valorLetras }}
              </div>
            </div>

            <div class="small-ref-row">
              <div class="info-cell extra-info">
                <label for="fOrdenExenta">No Correl. O. Compra Exenta</label>
                <input id="fOrdenExenta" v-model="invoice.ordenExenta" placeholder="Ingrese número" type="text">
              </div>
              <div class="info-cell extra-info">
                <label for="fRegistroExonerado">No Correl. C Registro Exonerado</label>
                <input id="fRegistroExonerado" v-model="invoice.registroExonerado" placeholder="Ingrese número" type="text">
              </div>
              <div class="info-cell extra-info">
                <label for="fSag">No Identificativo SAG</label>
                <input id="fSag" v-model="invoice.sag" placeholder="Ingrese número" type="text">
              </div>
            </div>

            <div class="signature-box">
              <div class="signature-area"></div>
              <div class="signature-label">FIRMA AUTORIZADA</div>
            </div>
          </div>

          <table class="totals-table">
            <tr><td>DESCUENTOS Y REBAJAS OTORGADOS L.</td><td>{{ toMoney(totals.descuentosOtorgados) }}</td></tr>
            <tr><td>SUB-TOTAL L.</td><td>{{ toMoney(totals.subTotal) }}</td></tr>
            <tr><td>IMPORTE EXENTO L.</td><td>{{ toMoney(totals.importeExento) }}</td></tr>
            <tr><td>IMPORTE EXONERADO L.</td><td>{{ toMoney(totals.importeExonerado) }}</td></tr>
            <tr><td>IMPORTE GRAVADO 15% L.</td><td>{{ toMoney(totals.importeGravado15) }}</td></tr>
            <tr><td>IMPORTE GRAVADO 18% L.</td><td>{{ toMoney(totals.importeGravado18) }}</td></tr>
            <tr><td>I.S.V. 15% L.</td><td>{{ toMoney(totals.isv15) }}</td></tr>
            <tr><td>I.S.V. 18% L.</td><td>{{ toMoney(totals.isv18) }}</td></tr>
            <tr>
              <td class="total-label"><strong>TOTAL A PAGAR L.</strong></td>
              <td class="total-value">{{ toMoney(totals.totalFinal) }}</td>
            </tr>
          </table>
        </div>

        <div class="footer-doc-info">
          <div>Original: Cliente</div>
          <div>Copia Obligado Tributario</div>
          <div>Modalidad Autoimpresor</div>
        </div>
      </div>
    </div>

    <datalist id="productList">
      <option v-for="(product, index) in config.products" :key="`${product.name}-option-${index}`" :value="product.name">
      </option>
    </datalist>
  </div>
</template>

<script>
const STORAGE_KEY = 'factura_v2_db'
const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:8082'

const DOCUMENT_TYPES = {
  '01': 'Factura',
  '02': 'Factura Prevalorada',
  '03': 'Ticket',
  '04': 'Recibo por Honorarios Profesionales',
  '05': 'Boleta de Compra',
  '06': 'Nota de Crédito',
  '07': 'Nota de Débito',
  '08': 'Comprobante de Retención',
  '09': 'Guía de Remisión',
}

const DEFAULT_CONFIG = {
  name: 'EMPRESA EJEMPLO',
  rtn: '0801199000000',
  email: '',
  direccion: '',
  cai: 'AAAAAA-BBBBBB-CCCCCC',
  fechaLimite: '',
  establecimiento: '001',
  puntoEmision: '001',
  tipoDocumento: '01',
  corr: 1,
  rangeFrom: '',
  rangeTo: '',
  logo: '',
  products: [],
}

function todayInputValue() {
  const date = new Date()
  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - offset * 60 * 1000)
  return localDate.toISOString().slice(0, 10)
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '')
}

function padLeft(value, length) {
  return digitsOnly(value).padStart(length, '0').slice(-length)
}

function normalizeCorrelativo(value) {
  let clean = digitsOnly(value)

  if (clean === '') return 1
  if (clean.length > 8) clean = clean.slice(0, 8)

  const number = Number(clean)
  if (number < 1) return 1
  if (number > 99999999) return 99999999

  return number
}

function extractLegacyPrefix(config) {
  const match = String(config.prefijo || '').match(/^(\d{3})-(\d{3})-(\d{2})-/)
  if (!match) return config

  return {
    ...config,
    establecimiento: config.establecimiento || match[1],
    puntoEmision: config.puntoEmision || match[2],
    tipoDocumento: config.tipoDocumento || match[3],
  }
}

function blankRow() {
  return {
    desc: '',
    qty: '',
    price: '',
    discount: '',
    taxType: 'gravado15',
  }
}

function normalizeProduct(product) {
  if (product.taxType) {
    return {
      name: product.name || '',
      price: Number(product.price) || 0,
      taxType: product.taxType,
    }
  }

  let taxType = 'gravado15'
  if (product.isv === 0) taxType = 'exento'
  if (product.isv === 0.15) taxType = 'gravado15'
  if (product.isv === 0.18) taxType = 'gravado18'

  return {
    name: product.name || '',
    price: Number(product.price) || 0,
    taxType,
  }
}

function normalizeConfig(saved = {}) {
  const migrated = extractLegacyPrefix(saved)
  const config = {
    ...DEFAULT_CONFIG,
    ...migrated,
    products: Array.isArray(migrated.products) ? migrated.products.map(normalizeProduct) : [],
  }

  config.establecimiento = padLeft(config.establecimiento, 3)
  config.puntoEmision = padLeft(config.puntoEmision, 3)
  config.tipoDocumento = padLeft(config.tipoDocumento, 2)
  config.corr = normalizeCorrelativo(config.corr)

  if (!config.logo) config.logo = ''
  if (!config.fechaLimite) config.fechaLimite = ''
  if (!config.email) config.email = ''
  if (!config.direccion) config.direccion = ''

  return config
}

function loadConfig() {
  let saved = {}

  try {
    saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    saved = {}
  }

  return normalizeConfig(saved)
}

function configFromEmpresa(empresa = {}) {
  return {
    name: empresa.nombre_empresa || '',
    rtn: empresa.rtn || '',
    email: empresa.email || '',
    direccion: empresa.direccion || '',
    cai: empresa.cai || '',
    fechaLimite: empresa.fecha_limite || '',
    establecimiento: empresa.establecimiento || '001',
    puntoEmision: empresa.punto_emision || '001',
    tipoDocumento: empresa.tipo_documento || '01',
    corr: empresa.correlativo || 1,
    rangeFrom: empresa.rango_desde || '',
    rangeTo: empresa.rango_hasta || '',
    logo: empresa.logo_base64 || '',
    products: Array.isArray(empresa.productos) ? empresa.productos.map(normalizeProduct) : [],
  }
}

function empresaFromConfig(config, id = 0) {
  return {
    id,
    nombre_empresa: config.name || '',
    direccion: config.direccion || '',
    email: config.email || '',
    rtn: config.rtn || '',
    cai: config.cai || '',
    fecha_limite: config.fechaLimite || '',
    establecimiento: padLeft(config.establecimiento, 3),
    punto_emision: padLeft(config.puntoEmision, 3),
    tipo_documento: padLeft(config.tipoDocumento, 2),
    correlativo: normalizeCorrelativo(config.corr),
    rango_desde: config.rangeFrom || '',
    rango_hasta: config.rangeTo || '',
    logo_base64: config.logo || '',
    productos: Array.isArray(config.products) ? config.products.map(normalizeProduct) : [],
  }
}

export default {
  name: 'App',
  data() {
    return {
      configVisible: false,
      facturasPanelVisible: false,
      facturas: [],
      facturasLoading: false,
      savingFactura: false,
      apiMessage: '',
      empresaID: 0,
      config: loadConfig(),
      invoice: {
        fecha: todayInputValue(),
        rtn: '',
        cliente: '',
        direccionCliente: '',
        formaPago: 'efectivo',
        fechaVencimiento: '',
        ordenExenta: '',
        registroExonerado: '',
        sag: '',
      },
      newProduct: {
        name: '',
        price: '',
        taxType: 'gravado15',
      },
      rows: Array.from({ length: 8 }, blankRow),
    }
  },
  mounted() {
    this.loadEmpresa()
    this.loadFacturas()
  },
  computed: {
    fiscalPrefix() {
      return `${padLeft(this.config.establecimiento, 3)}-${padLeft(this.config.puntoEmision, 3)}-${padLeft(this.config.tipoDocumento, 2)}-`
    },
    facturaNumero() {
      const correlativo = normalizeCorrelativo(this.config.corr).toString().padStart(8, '0')
      return `${this.fiscalPrefix}${correlativo}`
    },
    documentTypeLabel() {
      return DOCUMENT_TYPES[padLeft(this.config.tipoDocumento, 2)] || 'Documento Fiscal'
    },
    formattedFechaLimite() {
      return this.formatFechaDisplay(this.config.fechaLimite)
    },
    normalizedRangeFrom() {
      return String(this.config.rangeFrom || '').trim() || `${this.fiscalPrefix}00000001`
    },
    normalizedRangeTo() {
      return String(this.config.rangeTo || '').trim() || `${this.fiscalPrefix}99999999`
    },
    rangoTexto() {
      return `${this.normalizedRangeFrom} a ${this.normalizedRangeTo}`
    },
    deadlineStatus() {
      if (!this.config.fechaLimite) {
        return { hasDeadline: false, expired: false, daysLeft: null }
      }

      const [year, month, day] = String(this.config.fechaLimite).split('-').map(Number)
      if (!year || !month || !day) {
        return { hasDeadline: false, expired: false, daysLeft: null }
      }

      const today = new Date()
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())
      const deadline = new Date(year, month - 1, day)
      const daysLeft = Math.round((deadline - todayOnly) / (24 * 60 * 60 * 1000))

      return {
        hasDeadline: true,
        expired: daysLeft < 0,
        daysLeft,
      }
    },
    rangeInfo() {
      const fromNum = this.extractCorrelativeFromRange(this.normalizedRangeFrom)
      const toNum = this.extractCorrelativeFromRange(this.normalizedRangeTo)
      const corr = normalizeCorrelativo(this.config.corr)

      if (fromNum === null || toNum === null || fromNum > toNum) {
        return null
      }

      const totalDisponibles = toNum - fromNum + 1
      const restantes = toNum - corr + 1
      const porcentajeRestante = totalDisponibles > 0 ? (restantes / totalDisponibles) * 100 : 0

      return {
        fromNum,
        toNum,
        corr,
        totalDisponibles,
        restantes,
        porcentajeRestante,
      }
    },
    issuanceBlockReason() {
      if (this.deadlineStatus.expired) {
        return `Emisión bloqueada: la fecha límite venció el ${this.formatFechaDisplay(this.config.fechaLimite)}.`
      }

      if (!this.rangeInfo) {
        return 'Emisión bloqueada: configuración de rangos no válida.'
      }

      if (this.rangeInfo.corr < this.rangeInfo.fromNum || this.rangeInfo.corr > this.rangeInfo.toNum) {
        return 'Emisión bloqueada: el correlativo actual está fuera del rango autorizado.'
      }

      if (this.rangeInfo.restantes <= 0) {
        return 'Emisión bloqueada: el rango autorizado de facturación se agotó.'
      }

      return ''
    },
    isIssuanceBlocked() {
      return this.issuanceBlockReason !== ''
    },
    deadlineAlert() {
      if (!this.deadlineStatus.hasDeadline) return null

      if (this.deadlineStatus.expired) {
        return {
          expired: true,
          message: `La fecha límite de emisión venció el ${this.formatFechaDisplay(this.config.fechaLimite)}. La emisión de nuevos documentos está bloqueada.`,
        }
      }

      if (this.deadlineStatus.daysLeft <= 7) {
        return {
          expired: false,
          message: `Fecha límite de emisión por vencer: ${this.formatFechaDisplay(this.config.fechaLimite)}. Quedan ${this.deadlineStatus.daysLeft} día(s).`,
        }
      }

      return null
    },
    rangeAlert() {
      if (!this.rangeInfo) return null

      if (this.rangeInfo.restantes <= 0) {
        return {
          expired: true,
          message: 'El rango autorizado de facturación se ha agotado. No es posible emitir nuevos documentos.',
        }
      }

      if (this.rangeInfo.porcentajeRestante <= 5) {
        return {
          expired: false,
          message: `Rango de facturación próximo a agotarse. Quedan ${this.rangeInfo.restantes} números disponibles.`,
        }
      }

      return null
    },
    totals() {
      return this.rows.reduce((acc, row) => {
        const qty = this.toNumber(row.qty)
        const price = this.toNumber(row.price)
        const discount = this.toNumber(row.discount)
        const bruto = qty * price
        const descuentoAplicado = Math.min(discount, bruto)
        const baseNeta = bruto - descuentoAplicado

        let impuesto = 0

        acc.descuentosOtorgados += descuentoAplicado
        acc.subTotal += baseNeta

        if (row.taxType === 'exento') {
          acc.importeExento += baseNeta
        } else if (row.taxType === 'exonerado') {
          acc.importeExonerado += baseNeta
        } else if (row.taxType === 'gravado15') {
          acc.importeGravado15 += baseNeta
          impuesto = baseNeta * 0.15
          acc.isv15 += impuesto
        } else if (row.taxType === 'gravado18') {
          acc.importeGravado18 += baseNeta
          impuesto = baseNeta * 0.18
          acc.isv18 += impuesto
        }

        acc.totalFinal += baseNeta + impuesto
        return acc
      }, {
        descuentosOtorgados: 0,
        subTotal: 0,
        importeExento: 0,
        importeExonerado: 0,
        importeGravado15: 0,
        importeGravado18: 0,
        isv15: 0,
        isv18: 0,
        totalFinal: 0,
      })
    },
    valorLetras() {
      return this.totals.totalFinal > 0
        ? this.numeroALetras(this.totals.totalFinal)
        : 'CERO LEMPIRAS EXACTOS'
    },
  },
  watch: {
    'invoice.formaPago'(value) {
      if (value !== 'credito') {
        this.invoice.fechaVencimiento = ''
      }
    },
  },
  methods: {
    imprimir() {
      window.print()
    },
    toNumber(value) {
      const number = parseFloat(String(value ?? '').replace(/,/g, '').trim())
      return Number.isFinite(number) && number > 0 ? number : 0
    },
    toMoney(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    padLeft(value, length) {
      return padLeft(value, length)
    },
    limitCorrelativo() {
      this.config.corr = normalizeCorrelativo(this.config.corr)
    },
    validateRangePrefix(value) {
      const clean = String(value || '').trim()
      if (clean === '') return true
      return clean.startsWith(this.fiscalPrefix)
    },
    extractCorrelativeFromRange(rangeValue) {
      const clean = String(rangeValue || '').trim()
      if (!clean.startsWith(this.fiscalPrefix)) return null

      const correlative = digitsOnly(clean.slice(this.fiscalPrefix.length))
      if (correlative.length === 0) return null

      return Number(correlative)
    },
    autofillRangesIfEmpty() {
      if (!String(this.config.rangeFrom || '').trim()) {
        this.config.rangeFrom = `${this.fiscalPrefix}00000001`
      }

      if (!String(this.config.rangeTo || '').trim()) {
        this.config.rangeTo = `${this.fiscalPrefix}99999999`
      }
    },
    validateConfiguredRanges() {
      this.autofillRangesIfEmpty()

      if (!this.validateRangePrefix(this.config.rangeFrom)) {
        window.alert(`El rango desde debe iniciar con ${this.fiscalPrefix}`)
        return false
      }

      if (!this.validateRangePrefix(this.config.rangeTo)) {
        window.alert(`El rango hasta debe iniciar con ${this.fiscalPrefix}`)
        return false
      }

      const fromNum = this.extractCorrelativeFromRange(this.config.rangeFrom)
      const toNum = this.extractCorrelativeFromRange(this.config.rangeTo)
      const corr = normalizeCorrelativo(this.config.corr)

      if (fromNum === null) {
        window.alert('El rango desde no contiene un correlativo válido.')
        return false
      }

      if (toNum === null) {
        window.alert('El rango hasta no contiene un correlativo válido.')
        return false
      }

      if (fromNum > toNum) {
        window.alert('El rango desde no puede ser mayor que el rango hasta.')
        return false
      }

      if (corr < fromNum || corr > toNum) {
        window.alert(`El correlativo actual debe estar dentro del rango autorizado (${fromNum} a ${toNum}).`)
        return false
      }

      return true
    },
    async requestJSON(path, options = {}) {
      const response = await fetch(`${API_BASE_URL}${path}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
        ...options,
      })

      const payload = await response.json()
      if (!response.ok || payload.error) {
        throw new Error(payload.message || 'No se pudo completar la solicitud')
      }

      return payload
    },
    async loadEmpresa() {
      try {
        const payload = await this.requestJSON('/empresa')
        const empresa = payload.data?.empresa

        if (!empresa) return

        this.empresaID = empresa.id || 0
        this.config = normalizeConfig(configFromEmpresa(empresa))
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config))
      } catch (error) {
        this.apiMessage = error.message
      }
    },
    async saveEmpresaConfig() {
      const payload = await this.requestJSON('/empresa/save', {
        method: 'POST',
        body: JSON.stringify(empresaFromConfig(this.config, this.empresaID)),
      })
      const empresa = payload.data?.empresa

      if (empresa) {
        this.empresaID = empresa.id || this.empresaID
        this.config = normalizeConfig(configFromEmpresa(empresa))
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config))
      return empresa
    },
    toggleFacturasPanel() {
      this.facturasPanelVisible = !this.facturasPanelVisible
      if (this.facturasPanelVisible) {
        this.loadFacturas()
      }
    },
    formatFechaDisplay(fechaISO) {
      if (!fechaISO) return '---'
      const [year, month, day] = fechaISO.split('-')
      if (!year || !month || !day) return fechaISO
      return `${day}/${month}/${year}`
    },
    handleLogoUpload(event) {
      const [file] = event.target.files
      if (!file) return

      const reader = new FileReader()
      reader.onload = (readerEvent) => {
        this.config.logo = readerEvent.target.result
      }
      reader.readAsDataURL(file)
    },
    removeLogo() {
      this.config.logo = ''
      if (this.$refs.logoInput) {
        this.$refs.logoInput.value = ''
      }
    },
    getTaxLabel(taxType = 'gravado15') {
      const labels = {
        exento: 'Exento',
        exonerado: 'Exonerado',
        gravado15: 'Gravado 15%',
        gravado18: 'Gravado 18%',
      }
      return labels[taxType] || labels.gravado15
    },
    addProduct() {
      if (!this.newProduct.name || this.newProduct.price === '') {
        window.alert('Llena nombre y precio')
        return
      }

      this.config.products.push({
        name: this.newProduct.name,
        price: Number(this.newProduct.price) || 0,
        taxType: this.newProduct.taxType,
      })

      this.newProduct = {
        name: '',
        price: '',
        taxType: 'gravado15',
      }
    },
    removeProduct(index) {
      this.config.products.splice(index, 1)
    },
    addRow() {
      this.rows.push(blankRow())
    },
    handleSearch(row) {
      const product = this.config.products.find((item) => item.name === row.desc)

      if (product) {
        row.price = this.toMoney(product.price)
        row.taxType = product.taxType || 'gravado15'
      }
    },
    formatRowMoney(row, field) {
      if (String(row[field] ?? '').trim() === '') return
      row[field] = this.toMoney(this.toNumber(row[field]))
    },
    isEmptyRow(row) {
      return !String(row.desc || '').trim()
        && this.toNumber(row.qty) === 0
        && this.toNumber(row.price) === 0
        && this.toNumber(row.discount) === 0
    },
    rowTotal(row) {
      const qty = this.toNumber(row.qty)
      const price = this.toNumber(row.price)
      const discount = this.toNumber(row.discount)
      const bruto = qty * price
      const baseNeta = bruto - Math.min(discount, bruto)

      if (row.taxType === 'gravado15') return baseNeta * 1.15
      if (row.taxType === 'gravado18') return baseNeta * 1.18
      return baseNeta
    },
    async guardarTodo() {
      this.config.establecimiento = padLeft(this.config.establecimiento, 3)
      this.config.puntoEmision = padLeft(this.config.puntoEmision, 3)
      this.config.tipoDocumento = padLeft(this.config.tipoDocumento, 2)
      this.config.corr = normalizeCorrelativo(this.config.corr)
      this.autofillRangesIfEmpty()

      if (!this.validateConfiguredRanges()) {
        return
      }

      try {
        await this.saveEmpresaConfig()
        this.configVisible = false
        window.alert('Datos guardados.')
      } catch (error) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config))
        this.apiMessage = error.message
        window.alert(`No se pudo guardar en la base de datos: ${error.message}`)
      }
    },
    facturaPayload() {
      return {
        config: {
          ...this.config,
          products: this.config.products.map((product) => ({ ...product })),
        },
        invoice: { ...this.invoice },
        rows: this.rows.map((row) => ({ ...row })),
        totals: { ...this.totals },
        valorLetras: this.valorLetras,
        facturaNumero: this.facturaNumero,
        fiscalPrefix: this.fiscalPrefix,
        documentTypeLabel: this.documentTypeLabel,
        rangoTexto: this.rangoTexto,
        fechaLimite: this.formattedFechaLimite,
      }
    },
    async guardarFactura() {
      this.savingFactura = true
      this.apiMessage = ''

      try {
        const body = {
          numero: this.facturaNumero,
          fecha: this.invoice.fecha,
          cliente: this.invoice.cliente,
          rtn_cliente: this.invoice.rtn,
          total: Number(this.totals.totalFinal.toFixed(2)),
          payload: this.facturaPayload(),
        }

        await this.requestJSON('/facturas/save', {
          method: 'POST',
          body: JSON.stringify(body),
        })

        this.apiMessage = `Factura ${this.facturaNumero} guardada.`
        await this.loadFacturas()
      } catch (error) {
        this.apiMessage = error.message
        window.alert(error.message)
      } finally {
        this.savingFactura = false
      }
    },
    async loadFacturas() {
      this.facturasLoading = true

      try {
        const payload = await this.requestJSON('/facturas/all')
        this.facturas = payload.data?.facturas || []
      } catch (error) {
        this.apiMessage = error.message
      } finally {
        this.facturasLoading = false
      }
    },
    async verFactura(id) {
      try {
        const response = await this.requestJSON(`/facturas/${id}`)
        const factura = response.data?.factura
        const payload = factura?.payload

        if (!payload) {
          throw new Error('La factura seleccionada no tiene datos guardados')
        }

        const loadedConfig = extractLegacyPrefix(payload.config || {})
        this.config = {
          ...DEFAULT_CONFIG,
          ...loadedConfig,
          establecimiento: padLeft(loadedConfig.establecimiento, 3),
          puntoEmision: padLeft(loadedConfig.puntoEmision, 3),
          tipoDocumento: padLeft(loadedConfig.tipoDocumento, 2),
          corr: normalizeCorrelativo(loadedConfig.corr),
          products: Array.isArray(loadedConfig.products)
            ? loadedConfig.products.map(normalizeProduct)
            : [],
        }
        this.invoice = {
          fecha: payload.invoice?.fecha || todayInputValue(),
          rtn: payload.invoice?.rtn || '',
          cliente: payload.invoice?.cliente || '',
          direccionCliente: payload.invoice?.direccionCliente || '',
          formaPago: payload.invoice?.formaPago || 'efectivo',
          fechaVencimiento: payload.invoice?.fechaVencimiento || '',
          ordenExenta: payload.invoice?.ordenExenta || '',
          registroExonerado: payload.invoice?.registroExonerado || '',
          sag: payload.invoice?.sag || '',
        }
        this.rows = Array.isArray(payload.rows) && payload.rows.length
          ? payload.rows.map((row) => ({ ...blankRow(), ...row }))
          : Array.from({ length: 8 }, blankRow)

        this.apiMessage = `Factura ${factura.numero} cargada.`
        this.facturasPanelVisible = false
      } catch (error) {
        this.apiMessage = error.message
        window.alert(error.message)
      }
    },
    limpiarFactura() {
      this.invoice = {
        fecha: todayInputValue(),
        rtn: '',
        cliente: '',
        direccionCliente: '',
        formaPago: 'efectivo',
        fechaVencimiento: '',
        ordenExenta: '',
        registroExonerado: '',
        sag: '',
      }
      this.rows = Array.from({ length: 8 }, blankRow)
    },
    nuevaFactura() {
      if (this.issuanceBlockReason) {
        window.alert(this.issuanceBlockReason)
        return
      }

      if (window.confirm('¿Nuevo documento? Se incrementará el correlativo.')) {
        if (normalizeCorrelativo(this.config.corr) >= 99999999) {
          window.alert('El correlativo no puede ser mayor de 99,999,999.')
          return
        }

        if (this.rangeInfo && normalizeCorrelativo(this.config.corr) >= this.rangeInfo.toNum) {
          window.alert('Se alcanzó el final del rango autorizado de facturación. No es posible emitir más documentos.')
          return
        }

        this.config.corr = normalizeCorrelativo(this.config.corr) + 1
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config))
        this.saveEmpresaConfig().catch((error) => {
          this.apiMessage = error.message
        })
        this.limpiarFactura()
        this.apiMessage = ''
      }
    },
    numeroALetras(numero) {
      const parteEntera = Math.floor(numero)
      const centavos = Math.round((numero - parteEntera) * 100)
      return `${this.convertirNumero(parteEntera)} LEMPIRAS CON ${centavos.toString().padStart(2, '0')}/100`
    },
    convertirNumero(num) {
      const unidades = [
        '', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE',
        'DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE',
        'DIECIOCHO', 'DIECINUEVE', 'VEINTE',
      ]
      const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA']
      const centenas = [
        '', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS',
        'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS',
      ]

      if (num === 0) return 'CERO'
      if (num === 100) return 'CIEN'
      if (num <= 20) return unidades[num]

      if (num < 30) {
        const mapa = {
          21: 'VEINTIUNO',
          22: 'VEINTIDÓS',
          23: 'VEINTITRÉS',
          24: 'VEINTICUATRO',
          25: 'VEINTICINCO',
          26: 'VEINTISÉIS',
          27: 'VEINTISIETE',
          28: 'VEINTIOCHO',
          29: 'VEINTINUEVE',
        }
        return mapa[num]
      }

      if (num < 100) {
        const tens = Math.floor(num / 10)
        const rest = num % 10
        return decenas[tens] + (rest ? ` Y ${this.convertirNumero(rest)}` : '')
      }

      if (num < 1000) {
        const hundreds = Math.floor(num / 100)
        const rest = num % 100
        return centenas[hundreds] + (rest ? ` ${this.convertirNumero(rest)}` : '')
      }

      if (num < 1000000) {
        const thousands = Math.floor(num / 1000)
        const rest = num % 1000
        const thousandsText = thousands === 1 ? 'MIL' : `${this.convertirNumero(thousands)} MIL`
        return thousandsText + (rest ? ` ${this.convertirNumero(rest)}` : '')
      }

      if (num < 1000000000) {
        const millions = Math.floor(num / 1000000)
        const rest = num % 1000000
        const millionsText = millions === 1 ? 'UN MILLÓN' : `${this.convertirNumero(millions)} MILLONES`
        return millionsText + (rest ? ` ${this.convertirNumero(rest)}` : '')
      }

      return num.toString()
    },
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

:root {
  --primary: #1e3a8a;
  --secondary: #64748b;
  --success: #059669;
  --warning: #d97706;
  --danger: #dc2626;
  --bg-app: #f1f5f9;
  --border-color: #000;
}

* {
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

body {
  margin: 0;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background: var(--bg-app);
  color: #1e293b;
}

.app {
  max-width: 1200px;
  margin: 0 auto;
  padding: 18px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  background: white;
  padding: 14px 18px;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
  margin-bottom: 18px;
  position: sticky;
  top: 10px;
  z-index: 100;
}

.btn-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

button {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
}

button:active {
  transform: scale(0.95);
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.btn-primary {
  background: var(--primary);
  color: white;
}

.btn-secondary {
  background: var(--secondary);
  color: white;
}

.btn-success {
  background: var(--success);
  color: white;
}

.btn-danger {
  background: var(--danger);
  color: white;
}

.btn-warning {
  background: var(--warning);
  color: white;
}

.system-alert {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
}

.system-alert.expired {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #991b1b;
}

.config-panel {
  background: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 18px;
  border-left: 5px solid var(--primary);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.saved-invoices-panel {
  background: white;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 18px;
  border-left: 5px solid var(--success);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.panel-heading h3 {
  margin: 0 0 4px;
}

.panel-heading p {
  margin: 0;
  color: #475569;
  font-size: 13px;
}

.api-message {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 8px;
  color: #1e3a8a;
  font-size: 13px;
  font-weight: 600;
}

.saved-invoices-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-size: 12px;
}

.saved-invoices-table th,
.saved-invoices-table td {
  border: 1px solid #e2e8f0;
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}

.saved-invoices-table th {
  background: #f8fafc;
  color: #334155;
  text-transform: uppercase;
  font-size: 11px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.product-manager {
  background: #f8fafc;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow-x: auto;
}

.product-form {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.field label {
  font-size: 11px;
  font-weight: 800;
  color: #475569;
  text-transform: uppercase;
}

.field input,
.field textarea,
.field select {
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  min-width: 0;
}

.logo-config {
  max-width: 420px;
  margin-bottom: 20px;
}

.logo-preview-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}

.logo-preview-box {
  width: 180px;
  height: 90px;
  border: 1px dashed #94a3b8;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
}

.logo-preview-box img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.prod-list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: white;
  min-width: 620px;
}

.prod-list-table th,
.prod-list-table td {
  border: 1px solid #e2e8f0;
  padding: 8px;
  text-align: left;
  vertical-align: middle;
}

.invoice-container {
  background: white;
  padding: 24px;
  border-radius: 4px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto;
  overflow: hidden;
}

.invoice-border {
  border: 1.5px solid var(--border-color);
  padding: 8mm;
  height: 100%;
  overflow: hidden;
}

.header-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 14px;
  margin-bottom: 12px;
  align-items: stretch;
}

.company-header {
  display: flex;
  gap: 12px;
  align-items: stretch;
  min-width: 0;
}

.company-logo-box {
  width: 112px;
  min-width: 112px;
  height: 92px;
  border: 1px solid #cbd5e1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.company-logo-box img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.company-logo-placeholder {
  font-size: 10px;
  color: #64748b;
  text-align: center;
  padding: 6px;
}

.company-details {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px 0;
}

.company-details h1 {
  margin: 0 0 4px 0;
  font-size: 16px;
  color: #000;
  text-transform: uppercase;
  line-height: 1.2;
  word-break: break-word;
}

.company-details p {
  margin: 2px 0;
  font-size: 10px;
  line-height: 1.25;
  word-break: break-word;
}

.right-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.auth-box {
  border: 1.5px solid #000;
  padding: 7px 8px;
  font-size: 10px;
  line-height: 1.25;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.auth-box p {
  margin: 1px 0;
  word-break: break-word;
}

.factura-titulo {
  background: #000;
  color: white;
  text-align: center;
  padding: 5px;
  font-weight: bold;
  font-size: 18px;
  margin-top: 0;
}

.factura-numero {
  border: 1.5px solid #000;
  text-align: center;
  padding: 8px;
  font-size: 14px;
  font-weight: 900;
  margin-top: 0;
  word-break: break-word;
}

.client-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  margin-top: 10px;
}

.client-name {
  grid-column: span 2;
}

.payment-due {
  grid-column: span 2;
}

.info-cell {
  border: 1px solid #000;
  padding: 5px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.info-cell label {
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.info-cell input,
.info-cell select {
  border: none;
  width: 100%;
  font-size: 11px;
  outline: none;
  font-family: 'Inter', sans-serif;
  background: transparent;
  min-width: 0;
}

.text-center {
  text-align: center !important;
}

.text-right {
  text-align: right !important;
}

.invoice-table-wrap {
  width: 100%;
  overflow-x: hidden;
}

.items-table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
  table-layout: fixed;
}

.items-table th {
  background: #eef2ff;
  border: 1px solid #000;
  padding: 6px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  overflow-wrap: break-word;
}

.items-table td {
  border: 1px solid #000;
  padding: 3px 4px;
  overflow: hidden;
}

.items-table input,
.items-table select {
  width: 100%;
  max-width: 100%;
  border: none;
  outline: none;
  font-size: 11px;
  font-family: 'Inter', sans-serif;
  background: transparent;
  min-width: 0;
}

.items-table th:nth-child(1),
.items-table td:nth-child(1) {
  width: 34%;
}

.items-table th:nth-child(2),
.items-table td:nth-child(2) {
  width: 9%;
}

.items-table th:nth-child(3),
.items-table td:nth-child(3) {
  width: 13%;
}

.items-table th:nth-child(4),
.items-table td:nth-child(4) {
  width: 16%;
}

.items-table th:nth-child(5),
.items-table td:nth-child(5) {
  width: 14%;
}

.items-table th:nth-child(6),
.items-table td:nth-child(6) {
  width: 14%;
}

.row-total {
  text-align: right;
  font-weight: bold;
}

.manual-row {
  margin: 10px 0;
}

.totals-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 14px;
  margin-top: 14px;
  align-items: start;
}

.letters-box {
  border: 1px solid #000;
  padding: 10px;
  font-size: 10px;
}

.letters-value {
  margin-top: 5px;
  font-weight: bold;
  min-height: 32px;
}

.extra-info {
  border-top: none;
}

.small-ref-row .info-cell {
  padding: 4px 6px;
  font-size: 9px;
}

.small-ref-row .info-cell label {
  min-width: 170px;
  color: #111827;
  font-size: 9px;
  font-weight: 600;
  white-space: nowrap;
}

.small-ref-row .info-cell input {
  font-size: 9px;
  text-align: right;
}

.small-ref-row .info-cell input::placeholder {
  color: #94a3b8;
  text-align: left;
}

.extra-info label {
  min-width: 285px;
  font-size: 9px;
  white-space: normal;
}

.totals-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.totals-table td {
  border: 1px solid #000;
  padding: 5px 8px;
  font-size: 10px;
  font-family: 'Inter', sans-serif;
  overflow-wrap: break-word;
}

.totals-table td:first-child {
  font-weight: 700;
  text-align: right;
}

.totals-table td:last-child {
  text-align: right;
  font-weight: 600;
  width: 118px;
}

.total-label,
.total-value {
  font-size: 12px !important;
}

.signature-box {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.signature-area {
  width: 82%;
  height: 100px;
  background: transparent;
  border: none;
  margin-bottom: 8px;
}

.signature-label {
  width: 72%;
  border-top: 1px solid #000;
  text-align: center;
  padding-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #000;
}

.product-add-button {
  align-self: flex-end;
}

.table-button {
  padding: 4px 8px;
}

.config-actions {
  margin-top: 20px;
}

.footer-doc-info {
  margin-top: 18px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  font-size: 10px;
  font-weight: 600;
  color: #000;
}

.footer-doc-info div:nth-child(1) {
  text-align: left;
}

.footer-doc-info div:nth-child(2) {
  text-align: center;
}

.footer-doc-info div:nth-child(3) {
  text-align: right;
}

@media print {
  .toolbar,
  .config-panel,
  .saved-invoices-panel,
  .system-alert,
  .hide-print-tax,
  .no-print {
    display: none !important;
  }

  body {
    background: white;
  }

  .app {
    padding: 0;
  }

  .invoice-container {
    box-shadow: none;
    padding: 0;
    width: 100%;
  }

  .invoice-border {
    padding: 7mm;
  }

  .empty-row {
    display: none !important;
  }

  .items-table th:nth-child(1),
  .items-table td:nth-child(1) {
    width: 40% !important;
  }

  .items-table th:nth-child(2),
  .items-table td:nth-child(2) {
    width: 10% !important;
  }

  .items-table th:nth-child(3),
  .items-table td:nth-child(3) {
    width: 16% !important;
  }

  .items-table th:nth-child(4),
  .items-table td:nth-child(4) {
    width: 16% !important;
  }

  .items-table th:nth-child(6),
  .items-table td:nth-child(6) {
    width: 18% !important;
  }

  @page {
    size: portrait;
    margin: 9mm;
  }
}
</style>
